/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Per-project Hygraph model-id lookups, generated by
 * `schemas/cosmos-hygraph-schema-audit/scripts/generate-model-ids.ts` into the
 * `hygraph-model-ids/<alias>.json` files. The sections API resolves the maps for a request by the
 * site's Hygraph project id, so a section/hero/page typename can be mapped to its curated display
 * name and Hygraph model id without each route knowing its project alias.
 */

import cosmos from "./hygraph-model-ids/cosmos.json";
import deveg from "./hygraph-model-ids/deveg.json";
import ed from "./hygraph-model-ids/ed.json";
import eg from "./hygraph-model-ids/eg.json";
import eos from "./hygraph-model-ids/eos.json";
import kws from "./hygraph-model-ids/kws.json";
import lh from "./hygraph-model-ids/lh.json";
import mh from "./hygraph-model-ids/mh.json";
import qx from "./hygraph-model-ids/qx.json";
import rs from "./hygraph-model-ids/rs.json";
import tm from "./hygraph-model-ids/tm.json";
import ue from "./hygraph-model-ids/ue.json";

export interface ProjectModelIds {
	alias: string;
	name: string;
	projectId: string;
	environmentId: string;
	/** `apiId` → `id`, models only. */
	lookupModelId: Record<string, string>;
	/** `apiId` → `displayName`, models and components. */
	lookupDisplayName: Record<string, string>;
}

/** Every generated model-id file, each self-describing with its `projectId`. */
const MODEL_ID_FILES = [cosmos, deveg, ed, eg, eos, kws, lh, mh, qx, rs, tm, ue];

/**
 * Normalize a Hygraph project id for lookup — the files store bare 32-hex, routes pass a UUID.
 * Throws on malformed input so a garbage project id surfaces at lookup time rather than silently
 * missing the map and degrading every section to a humanized name with a null model id.
 */
const normalizeProjectId = (projectId: string): string => {
	const normalized = projectId.replace(/-/g, "").toLowerCase();
	if (!/^[0-9a-f]{32}$/.test(normalized)) {
		throw new Error(`Invalid Hygraph project id: '${projectId}' (expected a 32-hex UUID)`);
	}
	return normalized;
};

/** Model-id lookups keyed by normalized project id (the join key routes already carry). */
const MODEL_IDS_BY_PROJECT_ID: Record<string, ProjectModelIds> = Object.fromEntries(
	MODEL_ID_FILES.map((projectModelIds) => [normalizeProjectId(projectModelIds.projectId), projectModelIds]),
);

/** Resolve a project's model-id lookups by Hygraph project id; `undefined` for an unmapped project. */
export const getProjectModelIds = (projectId: string | undefined): ProjectModelIds | undefined =>
	projectId ? MODEL_IDS_BY_PROJECT_ID[normalizeProjectId(projectId)] : undefined;
